/**
 * Two temporal post-processing passes after face detection:
 *
 *  1. fillDetectionGaps  — interpolate position between two real detections
 *                          that bracket a gap of ≤ maxGap frames.
 *                          Only anchors with score ≥ minScore are used.
 *                          Ghost type: 'gap'
 *
 *  2. applyPersistence   — spread each high-confidence real detection
 *                          unconditionally ±frames frames in both directions.
 *                          Only detections with score ≥ minScore are spread.
 *                          Ghost type: 'persist'
 *
 * Ghost type is stored in detection.ghost so the debug overlay can colour
 * each category differently (real = red, gap = orange, persist = purple).
 */

export function fillDetectionGaps(detectionMap, totalFrames, maxGap = 8, minScore = 0.35) {
  const result = new Map();
  for (const [f, dets] of detectionMap) result.set(f, dets.map(d => ({ ...d })));

  for (let f = 0; f < totalFrames; f++) {
    const dets = detectionMap.get(f);
    if (!dets?.length) continue;

    for (const det of dets) {
      if (det.score < minScore) continue; // anchor needs enough confidence

      let nextF = -1, nextDet = null;
      for (let d = 1; d <= maxGap + 1 && nextF < 0; d++) {
        const nd = detectionMap.get(f + d);
        const match = nd?.find(n => n.score >= minScore && sameFace(det, n));
        if (match) { nextF = f + d; nextDet = match; }
      }

      if (nextF < 0 || nextF - f < 2) continue;

      for (let gf = f + 1; gf < nextF; gf++) {
        const t   = (gf - f) / (nextF - f);
        const box = { ...lerpBox(det, nextDet, t), ghost: 'gap' };

        const existing = result.get(gf) ?? [];
        if (!existing.some(e => sameFace(e, box))) {
          result.set(gf, [...existing, box]);
        }
      }
    }
  }

  return result;
}

export function applyPersistence(detectionMap, totalFrames, frames = 3, minScore = 0.45) {
  const result = new Map();
  for (const [f, dets] of detectionMap) result.set(f, [...dets]);

  for (let f = 0; f < totalFrames; f++) {
    // Only spread real, high-confidence detections
    const real = (detectionMap.get(f) ?? []).filter(d => !d.ghost && d.score >= minScore);
    if (!real.length) continue;

    for (let d = 1; d <= frames; d++) {
      for (const gf of [f - d, f + d]) {
        if (gf < 0 || gf >= totalFrames) continue;

        const existing = result.get(gf) ?? [];
        const toAdd    = real.filter(det => !existing.some(e => sameFace(e, det)));
        if (toAdd.length) {
          result.set(gf, [...existing, ...toAdd.map(det => ({ ...det, ghost: 'persist' }))]);
        }
      }
    }
  }

  return result;
}

function sameFace(a, b) {
  const ax = (a.x1 + a.x2) / 2, ay = (a.y1 + a.y2) / 2;
  const bx = (b.x1 + b.x2) / 2, by = (b.y1 + b.y2) / 2;
  const avgW = ((a.x2 - a.x1) + (b.x2 - b.x1)) / 2;
  const avgH = ((a.y2 - a.y1) + (b.y2 - b.y1)) / 2;
  return Math.abs(ax - bx) < avgW * 0.7 && Math.abs(ay - by) < avgH * 0.7;
}

function lerpBox(a, b, t) {
  return {
    x1:    a.x1 + (b.x1 - a.x1) * t,
    y1:    a.y1 + (b.y1 - a.y1) * t,
    x2:    a.x2 + (b.x2 - a.x2) * t,
    y2:    a.y2 + (b.y2 - a.y2) * t,
    score: Math.min(a.score, b.score),
  };
}
